import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useUser } from '../contexts/UserContext';
import { AffiliateService } from '../services/stripeAffiliate';
import { showToast } from '../Toast';

export default function Parceiros() {
  const { userData, userId } = useUser();
  const [loading, setLoading] = useState(true);
  const [affiliateData, setAffiliateData] = useState(null);
  const [commissions, setCommissions] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [activeTab, setActiveTab] = useState('overview'); // overview, commissions, payouts

  useEffect(() => {
    if (userId) {
      loadAffiliateData();
    }
  }, [userId]);

  const loadAffiliateData = async () => {
    setLoading(true);
    try {
      const data = await AffiliateService.getAffiliateStatus(userId);
      setAffiliateData(data);
      
      if (data) {
        // Carregar dados relacionados se for afiliado
        const [commissionsData, payoutsData] = await Promise.all([
          AffiliateService.getCommissions(data.id),
          AffiliateService.getPayouts(data.id)
        ]);
        setCommissions(commissionsData || []);
        setPayouts(payoutsData || []);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de afiliado:', error);
      showToast('Erro ao carregar informações de parceiro.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAffiliate = async () => {
    try {
      setLoading(true);
      const newAffiliate = await AffiliateService.createAffiliateProfile(userId, userData?.nome || userData?.email);
      setAffiliateData(newAffiliate);
      showToast('Conta de parceiro ativada com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao criar conta de parceiro:', error);
      showToast('Não foi possível ativar sua conta de parceiro.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectStripe = async () => {
    if (!affiliateData) return;
    try {
      showToast('Iniciando conexão com Stripe...', 'info');
      const { url } = await AffiliateService.connectStripe(affiliateData.id);
      
      if (url) {
        window.location.href = url;
      } else {
        showToast('Erro ao obter link do Stripe.', 'error');
      }
    } catch (error) {
      console.error('Erro ao conectar Stripe:', error);
      showToast('Erro ao iniciar conexão com Stripe.', 'error');
    }
  };

  const handleRequestPayout = async () => {
    if (!affiliateData) return;
    if (affiliateData.balance_available <= 0) {
      showToast('Saldo insuficiente para saque.', 'error');
      return;
    }
    
    try {
      await AffiliateService.requestPayout(affiliateData.id, affiliateData.balance_available);
      showToast('Saque solicitado com sucesso!', 'success');
      loadAffiliateData(); // Recarregar saldo
    } catch (error) {
      console.error('Erro ao solicitar saque:', error);
      showToast('Erro ao processar solicitação de saque.', 'error');
    }
  };

  const copyLink = () => {
    if (!affiliateData) return;
    const link = `${window.location.origin}/signup?ref=${affiliateData.referral_code}`;
    navigator.clipboard.writeText(link);
    showToast('Link copiado para a área de transferência!', 'success');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#64748b' }}>
        <Icon icon="mdi:loading" width="32" className="animate-spin" />
      </div>
    );
  }

  // TELA DE ATIVAÇÃO (Se não for afiliado)
  if (!affiliateData) {
    return (
      <div style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Inter', sans-serif" }}>
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <div style={{ 
            width: '80px', height: '80px', backgroundColor: '#eff6ff', borderRadius: '24px', 
            display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' 
          }}>
            <Icon icon="ph:gift" width="40" color="#0ea5e9" />
          </div>
          <h1 style={{ fontSize: '32px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' }}>
            Indique e Ganhe
          </h1>
          <p style={{ color: '#64748b', fontSize: '18px', maxWidth: '600px', margin: '0 auto' }}>
            Torne-se um parceiro oficial, compartilhe seu link exclusivo e ganhe comissões por cada novo cliente que assinar nossos planos.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginBottom: '48px' }}>
          {[
            { icon: 'ph:link', title: 'Compartilhe seu Link', desc: 'Gere um link único para divulgar em suas redes.' },
            { icon: 'ph:currency-circle-dollar', title: 'Receba Comissões', desc: 'Ganhe % recorrente sobre a assinatura dos indicados.' },
            { icon: 'ph:bank', title: 'Saque Automático', desc: 'Receba diretamente na sua conta bancária via Stripe.' }
          ].map((item, i) => (
            <div key={i} style={{ padding: '24px', backgroundColor: 'white', borderRadius: '16px', border: '1px solid #e2e8f0' }}>
              <Icon icon={item.icon} width="32" color="#0ea5e9" style={{ marginBottom: '16px' }} />
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1e293b', marginBottom: '8px' }}>{item.title}</h3>
              <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.5' }}>{item.desc}</p>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center' }}>
          <button 
            onClick={handleCreateAffiliate}
            style={{
              backgroundColor: '#0ea5e9', color: 'white', padding: '16px 32px', borderRadius: '12px',
              fontSize: '16px', fontWeight: '600', border: 'none', cursor: 'pointer',
              boxShadow: '0 4px 6px -1px rgba(14, 165, 233, 0.2)'
            }}
          >
            Ativar Minha Conta de Parceiro
          </button>
        </div>
      </div>
    );
  }

  // DASHBOARD DE AFILIADO
  return (
    <div style={{ padding: '24px', maxWidth: '100%', margin: '0', fontFamily: "'Inter', sans-serif" }}>
      {/* Header com Métricas */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px', flexWrap: 'wrap', gap: '24px' }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a', marginBottom: '8px' }}>Painel de Parceiro</h1>
          <p style={{ color: '#64748b' }}>Gerencie suas indicações e saques.</p>
        </div>
        
        <div style={{ display: 'flex', gap: '12px' }}>
            <PayoutSettingsModal affiliateData={affiliateData} onUpdate={() => loadAffiliateData()} />
            <div style={{ backgroundColor: '#f0f9ff', border: '1px solid #b3e5fc', padding: '12px 16px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon icon="mdi:key-variant" color="#0288d1" width="24" />
                <div>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#01579b' }}>PIX</p>
                <p style={{ fontSize: '12px', color: '#0277bd' }}>Configurar chave PIX e verificar conta do parceiro.</p>
                </div>
            </div>
        </div>
      </div>

      {/* Cards de Métricas */}
      {(affiliateData.approval_status === 'approved' && affiliateData.pix_verification_status === 'verified') && (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '24px', marginBottom: '32px' }}>
        <MetricCard 
          title="Saldo Disponível" 
          value={`R$ ${affiliateData.balance_available?.toFixed(2)}`} 
          icon="ph:wallet" 
          color="#10b981"
          action={
            <button 
              disabled={!affiliateData.balance_available || affiliateData.balance_available <= 0}
              onClick={handleRequestPayout}
              style={{ 
                marginTop: '12px', width: '100%', padding: '8px', border: '1px solid #e2e8f0', borderRadius: '6px',
                backgroundColor: 'white', color: '#0f172a', fontSize: '13px', fontWeight: '500', cursor: 'pointer',
                opacity: (!affiliateData.balance_available || affiliateData.balance_available <= 0) ? 0.5 : 1
              }}
            >
              Solicitar Saque
            </button>
          }
        />
        <MetricCard title="Saldo Pendente" value={`R$ ${affiliateData.balance_pending?.toFixed(2)}`} icon="ph:hourglass" color="#f59e0b" />
        <MetricCard title="Total Ganho" value={`R$ ${affiliateData.total_earnings?.toFixed(2)}`} icon="ph:trend-up" color="#0ea5e9" />
        <MetricCard title="Total Cliques" value={affiliateData.clicks_count || 0} icon="ph:cursor-click" color="#8b5cf6" />
      </div>
      )}

      {/* Analytics de Cliques (Topo) */}
      {(affiliateData.approval_status === 'approved' && affiliateData.pix_verification_status === 'verified') && (
        <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', marginBottom: '16px' }}>Analytics de Cliques</h3>
          <ClicksAnalytics affiliateId={affiliateData.id} />
        </div>
      )}

      {/* Área de Link */}
      {(affiliateData.approval_status === 'approved' && affiliateData.pix_verification_status === 'verified') && (
        <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '32px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', marginBottom: '16px' }}>Seu Link de Indicação</h3>
          <ReferralLinkEditor affiliateData={affiliateData} onUpdate={() => loadAffiliateData()} />
        </div>
      )}

      {/* Configuração PIX */}
      <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '32px' }}>
        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a', marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icon icon="mdi:key-variant" /> Dados do Parceiro (PIX)
          <StatusBadge status={affiliateData.approval_status || 'pending'} />
        </h3>

        <PixSettingsForm affiliateData={affiliateData} onUpdated={() => loadAffiliateData()} />
      </div>

      {/* Gate de aprovação */}
      {(affiliateData.approval_status !== 'approved' || affiliateData.pix_verification_status !== 'verified') && (
        <div style={{ backgroundColor: '#fff7ed', border: '1px solid #fed7aa', padding: '12px 16px', borderRadius: '8px', marginBottom: '16px', color: '#9a3412', fontSize: '13px' }}>
          {affiliateData.approval_status !== 'approved' ? 'Sua conta de parceiro está em análise. Após aprovação, valide sua chave PIX.' : 'Conclua a validação do PIX para liberar todas as funções.'}
        </div>
      )}

      {/* Tabs */}
      {(affiliateData.approval_status === 'approved' && affiliateData.pix_verification_status === 'verified') && (
        <div style={{ borderBottom: '1px solid #e2e8f0', marginBottom: '24px', display: 'flex', gap: '24px' }}>
          <TabButton active={activeTab === 'overview'} onClick={() => setActiveTab('overview')} label="Visão Geral" />
          <TabButton active={activeTab === 'commissions'} onClick={() => setActiveTab('commissions')} label="Comissões" />
          <TabButton active={activeTab === 'payouts'} onClick={() => setActiveTab('payouts')} label="Saques" />
        </div>
      )}

      {/* Conteúdo das Tabs */}
      {(affiliateData.approval_status === 'approved' && affiliateData.pix_verification_status === 'verified') && (
      <div style={{ backgroundColor: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}>
        {activeTab === 'overview' && (
          <div style={{ padding: '24px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Últimas Comissões</h3>
            {commissions.length > 0 ? (
               <CommissionsTable commissions={commissions.slice(0, 5)} />
            ) : (
              <EmptyState message="Nenhuma comissão registrada ainda." />
            )}

            <div style={{ marginTop: '24px' }}>
              <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px' }}>Analytics de Cliques</h3>
              <ClicksAnalytics affiliateId={affiliateData.id} />
            </div>
          </div>
        )}

        {activeTab === 'commissions' && (
          <div style={{ padding: '24px' }}>
             {(affiliateData.approval_status === 'approved' && affiliateData.pix_verification_status === 'verified') ? (
               commissions.length > 0 ? (
               <CommissionsTable commissions={commissions} />
              ) : (
                <EmptyState message="Nenhuma comissão registrada ainda." />
              )
            ) : (
              <EmptyState message="Finalize a validação do PIX para visualizar." />
            )}
          </div>
        )}

        {activeTab === 'payouts' && (
          <div style={{ padding: '24px' }}>
             {(affiliateData.approval_status === 'approved' && affiliateData.pix_verification_status === 'verified') ? (
               payouts.length > 0 ? (
                 <PayoutsTable payouts={payouts} />
               ) : (
                 <EmptyState message="Nenhum saque solicitado ainda." />
               )
             ) : (
               <EmptyState message="Finalize a validação do PIX para solicitar saques." />
             )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}

// Subcomponentes

function ReferralLinkEditor({ affiliateData, onUpdate }) {
  const [isEditing, setIsEditing] = useState(false);
  const [newCode, setNewCode] = useState(affiliateData.referral_code);
  const [loading, setLoading] = useState(false);

  const handleSave = async () => {
    if (!newCode || newCode === affiliateData.referral_code) {
      setIsEditing(false);
      return;
    }
    
    setLoading(true);
    try {
      await AffiliateService.updateReferralCode(newCode);
      showToast('Código atualizado com sucesso!', 'success');
      onUpdate();
      setIsEditing(false);
    } catch (error) {
      console.error(error);
      showToast(error.message || 'Erro ao atualizar código', 'error');
    } finally {
      setLoading(false);
    }
  };

  if (isEditing) {
    return (
      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '0 12px' }}>
            <span style={{ color: '#64748b', fontSize: '14px', whiteSpace: 'nowrap' }}>{window.location.origin}/signup?ref=</span>
            <input 
                value={newCode}
                onChange={(e) => setNewCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))}
                style={{ flex: 1, border: 'none', background: 'transparent', padding: '12px 0', outline: 'none', fontSize: '14px', fontFamily: 'monospace', color: '#0f172a', fontWeight: '600' }}
                placeholder="SEUCODIGO"
                maxLength={20}
            />
        </div>
        <button onClick={handleSave} disabled={loading} style={{ backgroundColor: '#10b981', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer', opacity: loading ? 0.7 : 1 }}>
            <Icon icon="ph:check-bold" />
        </button>
        <button onClick={() => setIsEditing(false)} style={{ backgroundColor: '#ef4444', color: 'white', border: 'none', padding: '10px 16px', borderRadius: '8px', cursor: 'pointer' }}>
            <Icon icon="ph:x-bold" />
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
        <div style={{ flex: 1, backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '12px 16px', color: '#64748b', fontSize: '14px', fontFamily: 'monospace', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span>{`${window.location.origin}/signup?ref=${affiliateData.referral_code}`}</span>
            <button onClick={() => setIsEditing(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#64748b' }} title="Editar código">
                <Icon icon="ph:pencil-simple" />
            </button>
        </div>
        <button 
        onClick={() => {
            navigator.clipboard.writeText(`${window.location.origin}/signup?ref=${affiliateData.referral_code}`);
            showToast('Link copiado!', 'success');
        }}
        style={{ backgroundColor: '#0ea5e9', color: 'white', border: 'none', padding: '0 24px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', height: '42px' }}
        >
        <Icon icon="ph:copy" />
        Copiar
        </button>
    </div>
  );
}

function PixSettingsForm({ affiliateData, onUpdated }) {
  const [form, setForm] = useState({
    pix_key: affiliateData.pix_key || '',
    pix_key_type: affiliateData.pix_key_type || 'cpf_cnpj'
  });
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await AffiliateService.updatePixKey(affiliateData.id, form);
      showToast('Dados salvos!', 'success');
      onUpdated();
    } catch (error) {
      console.error(error);
      showToast('Erro ao salvar dados do parceiro', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleVerify = async () => {
    if (affiliateData.approval_status !== 'approved') {
      showToast('Sua conta precisa ser aprovada antes de validar a chave PIX', 'warning');
      return;
    }
    if (!form.pix_key) {
      showToast('Informe sua chave PIX para verificar', 'warning');
      return;
    }
    setVerifying(true);
    try {
      await AffiliateService.validatePixKey({
        userId: affiliateData.user_id,
        affiliateId: affiliateData.id,
        pix_key: form.pix_key,
        pix_key_type: form.pix_key_type
      });
      await AffiliateService.initiatePixVerification(affiliateData.id);
      showToast('Verificação iniciada. Aguarde aprovação do admin.', 'success');
      onUpdated();
    } catch (error) {
      console.error(error);
      showToast('Erro ao iniciar verificação PIX', 'error');
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '240px 1fr', gap: '12px' }}>
      <div>
        <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Tipo de Chave PIX</label>
        <select 
          value={form.pix_key_type}
          onChange={e => setForm({ ...form, pix_key_type: e.target.value })}
          style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
        >
          <option value="cpf_cnpj">CPF/CNPJ</option>
          <option value="email">E-mail</option>
          <option value="phone">Telefone</option>
          <option value="random">Aleatória</option>
        </select>
      </div>
      <div>
        <label style={{ display: 'block', fontSize: '12px', color: '#64748b', marginBottom: '6px' }}>Chave PIX</label>
        <input 
          value={form.pix_key} 
          onChange={e => setForm({ ...form, pix_key: e.target.value })}
          placeholder="Celular, e-mail, aleatória ou CPF/CNPJ"
          style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '8px' }}
        />
      </div>

      <div style={{ gridColumn: '1 / -1', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
        <button onClick={handleSave} disabled={saving} style={{ padding: '10px 16px', border: 'none', borderRadius: '8px', backgroundColor: '#0ea5e9', color: 'white', cursor: 'pointer' }}>{saving ? 'Salvando...' : 'Salvar Dados'}</button>
        <button onClick={handleVerify} disabled={verifying || affiliateData.approval_status !== 'approved'} style={{ padding: '10px 16px', border: 'none', borderRadius: '8px', backgroundColor: '#10b981', color: 'white', cursor: 'pointer', opacity: affiliateData.approval_status !== 'approved' ? 0.7 : 1 }}>{verifying ? 'Enviando...' : (affiliateData.approval_status !== 'approved' ? 'Aguarde aprovação da conta' : 'Iniciar Verificação PIX')}</button>
      </div>

      <div style={{ gridColumn: '1 / -1', marginTop: '12px', padding: '12px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '8px', color: '#9a3412', fontSize: '12px' }}>
        Primeiro sua conta é aprovada pelo administrador. Depois, valide sua chave PIX. A chave PIX deve pertencer ao mesmo CPF/CNPJ do seu cadastro quando o tipo for CPF/CNPJ.
      </div>
    </div>
  );
}
function PayoutSettingsModal({ affiliateData, onUpdate }) {
    const [isOpen, setIsOpen] = useState(false);
    const [minAmount, setMinAmount] = useState(affiliateData.min_payout_amount || 50);
    const [schedule, setSchedule] = useState(affiliateData.payout_schedule || 'monthly');
    const [loading, setLoading] = useState(false);

    const handleSave = async () => {
        setLoading(true);
        try {
            await AffiliateService.updatePayoutSettings(affiliateData.id, minAmount, schedule);
            showToast('Configurações salvas!', 'success');
            onUpdate();
            setIsOpen(false);
        } catch (error) {
            console.error(error);
            showToast('Erro ao salvar configurações', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) {
        return (
            <button onClick={() => setIsOpen(true)} style={{ backgroundColor: 'white', border: '1px solid #e2e8f0', padding: '8px 12px', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#64748b', fontSize: '13px' }}>
                <Icon icon="ph:gear" width="18" />
                Configurar Saque
            </button>
        );
    }

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', width: '100%', maxWidth: '400px' }}>
                <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600' }}>Configurações de Pagamento (PIX Manual)</h3>
                
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Valor Mínimo para Saque Automático</label>
                    <div style={{ display: 'flex', alignItems: 'center', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '0 12px' }}>
                        <span style={{ color: '#64748b' }}>R$</span>
                        <input 
                            type="number" 
                            value={minAmount} 
                            onChange={e => setMinAmount(Number(e.target.value))}
                            style={{ border: 'none', padding: '10px', width: '100%', outline: 'none' }} 
                        />
                    </div>
                </div>

                <div style={{ marginBottom: '24px' }}>
                    <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '500' }}>Frequência de Pagamento</label>
                    <select 
                        value={schedule} 
                        onChange={e => setSchedule(e.target.value)}
                        style={{ width: '100%', padding: '10px', border: '1px solid #e2e8f0', borderRadius: '6px', outline: 'none' }}
                    >
                        <option value="monthly">Mensal (Dia 1)</option>
                    </select>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button onClick={() => setIsOpen(false)} style={{ padding: '8px 16px', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: 'white', cursor: 'pointer' }}>Cancelar</button>
                    <button onClick={handleSave} disabled={loading} style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', backgroundColor: '#0f172a', color: 'white', cursor: 'pointer' }}>
                        {loading ? 'Salvando...' : 'Salvar'}
                    </button>
                </div>
            </div>
        </div>
    );
}

function MetricCard({ title, value, icon, color, action }) {
  return (
    <div style={{ backgroundColor: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
        <div>
          <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>{title}</p>
          <p style={{ fontSize: '24px', fontWeight: '700', color: '#0f172a' }}>{value}</p>
        </div>
        <div style={{ width: '40px', height: '40px', borderRadius: '10px', backgroundColor: `${color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon icon={icon} width="20" color={color} />
        </div>
      </div>
      {action}
    </div>
  );
}

function TabButton({ active, onClick, label }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '12px 0',
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid #0ea5e9' : '2px solid transparent',
        color: active ? '#0ea5e9' : '#64748b',
        fontWeight: active ? '600' : '500',
        cursor: 'pointer',
        fontSize: '14px',
        transition: 'all 0.2s'
      }}
    >
      {label}
    </button>
  );
}

function EmptyState({ message }) {
  return (
    <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
      <Icon icon="ph:magnifying-glass" width="32" style={{ marginBottom: '8px', opacity: 0.5 }} />
      <p>{message}</p>
    </div>
  );
}

function ClicksAnalytics({ affiliateId }) {
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const data = await AffiliateService.getClicks(affiliateId, 20);
        setClicks(data || []);
      } catch (e) {
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [affiliateId]);

  if (loading) {
    return <div style={{ padding: '12px', color: '#64748b' }}>Carregando...</div>;
  }

  if (!clicks.length) {
    return <EmptyState message="Nenhum clique registrado." />;
  }

  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>DATA</th>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>IP</th>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>USER AGENT</th>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>REFERER</th>
        </tr>
      </thead>
      <tbody>
        {clicks.map((c) => (
          <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={{ padding: '12px', fontSize: '14px', color: '#334155' }}>{new Date(c.created_at).toLocaleString('pt-BR')}</td>
            <td style={{ padding: '12px', fontSize: '12px', color: '#334155' }}>{c.ip_address || '-'}</td>
            <td style={{ padding: '12px', fontSize: '12px', color: '#334155', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.user_agent || '-'}</td>
            <td style={{ padding: '12px', fontSize: '12px', color: '#334155' }}>{c.referer || '-'}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
function CommissionsTable({ commissions }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>DATA</th>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>VALOR</th>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>STATUS</th>
        </tr>
      </thead>
      <tbody>
        {commissions.map((c) => (
          <tr key={c.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={{ padding: '12px', fontSize: '14px', color: '#334155' }}>
              {new Date(c.created_at).toLocaleDateString('pt-BR')}
            </td>
            <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#10b981' }}>
              + R$ {c.amount.toFixed(2)}
            </td>
            <td style={{ padding: '12px' }}>
              <StatusBadge status={c.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function PayoutsTable({ payouts }) {
  return (
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
      <thead>
        <tr style={{ borderBottom: '1px solid #e2e8f0' }}>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>DATA</th>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>VALOR</th>
          <th style={{ textAlign: 'left', padding: '12px', fontSize: '12px', color: '#64748b', fontWeight: '600' }}>STATUS</th>
        </tr>
      </thead>
      <tbody>
        {payouts.map((p) => (
          <tr key={p.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
            <td style={{ padding: '12px', fontSize: '14px', color: '#334155' }}>
              {new Date(p.created_at).toLocaleDateString('pt-BR')}
            </td>
            <td style={{ padding: '12px', fontSize: '14px', fontWeight: '600', color: '#f59e0b' }}>
              - R$ {p.amount.toFixed(2)}
            </td>
            <td style={{ padding: '12px' }}>
              <StatusBadge status={p.status} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function StatusBadge({ status }) {
  const styles = {
    pending: { bg: '#fff7ed', color: '#c2410c', label: 'Pendente' },
    available: { bg: '#ecfdf5', color: '#047857', label: 'Disponível' },
    paid: { bg: '#eff6ff', color: '#1d4ed8', label: 'Pago' },
    processing: { bg: '#fff7ed', color: '#c2410c', label: 'Processando' },
    failed: { bg: '#fef2f2', color: '#b91c1c', label: 'Falhou' },
    approved: { bg: '#ecfdf5', color: '#047857', label: 'Aprovado' },
    rejected: { bg: '#fef2f2', color: '#b91c1c', label: 'Rejeitado' },
    verified: { bg: '#e8f5e9', color: '#2e7d32', label: 'Verificado' }
  };

  const style = styles[status] || { bg: '#f1f5f9', color: '#64748b', label: status };

  return (
    <span style={{ 
      backgroundColor: style.bg, 
      color: style.color, 
      padding: '4px 8px', 
      borderRadius: '4px', 
      fontSize: '12px', 
      fontWeight: '600',
      textTransform: 'uppercase'
    }}>
      {style.label}
    </span>
  );
}
